// src/pages/Agents/AIAgents.tsx — AI voice agent registry (Phase 4).
import React, { useCallback, useEffect, useState } from 'react';
import { BusinessSwitcher } from 'components/Common/BusinessSwitcher';
import { Bot, Plus, RefreshCw, Trash2, AlertCircle, Link2, Server, ChevronDown, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { aiAgentsService, AiAgent, RuntimeAgent } from 'api/services/aiAgents.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';

const AIAgents: React.FC = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AiAgent[]>([]);
  // Which business a service provider is looking at. Empty for a business user —
  // their own organisation is the only one, and the switcher renders nothing.
  const [orgId, setOrgId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Agents that exist on the runtime but not here. Kept separate from `error`:
  // no provider / no linked business is a normal state, not a page failure.
  const [runtime, setRuntime] = useState<RuntimeAgent[]>([]);
  const [runtimeNote, setRuntimeNote] = useState<string | null>(null);
  const [runtimeLoading, setRuntimeLoading] = useState(false);
  const [showRuntime, setShowRuntime] = useState(false);

  const PAGE = 200;
  const [runtimeLoaded, setRuntimeLoaded] = useState(false);
  const [viewing, setViewing] = useState<{ uuid: string; json: string } | null>(null);
  const [busyUuid, setBusyUuid] = useState<string | null>(null);
  // Open one runtime agent by id. The list above shows only the most recent agents that
  // carry a business reference, so an older one (e.g. one an IVR state points at) can
  // only be reached by its id. Accepts the bare uuid or the whole wss URL from a flow.
  const [byId, setById] = useState('');
  const byIdUuid = (byId.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i) || [])[0]?.toLowerCase() || null;

  const loadRuntime = useCallback(async () => {
    setRuntimeLoading(true); setRuntimeNote(null);
    const res = await aiAgentsService.listRuntimeOnly(undefined, PAGE, 0);
    if (res.success) setRuntime(res.data || []);
    else { setRuntime([]); setRuntimeNote(res.error || 'Could not reach the runtime'); }
    setRuntimeLoaded(true);
    setRuntimeLoading(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    // Passed explicitly. Without it the API falls back to the caller's own
    // organisation — for a service provider that is their SERVICE org, which owns no
    // agents, so the page showed an empty list that looked like a fault.
    const res = await aiAgentsService.list(orgId || undefined);
    if (res.success) setAgents(res.data || []);
    else setError(res.error || 'Failed to load agents');
    setLoading(false);
    // Deliberately NOT fetched on mount: a business can own six figures of
    // runtime agents, so this is loaded only when the section is opened.
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const toggleRuntime = () => {
    const opening = !showRuntime;
    setShowRuntime(opening);
    if (opening && !runtimeLoaded) loadRuntime();
  };

  const viewRuntime = async (uuid: string) => {
    setBusyUuid(uuid);
    const res = await aiAgentsService.viewRuntimeAgent(uuid);
    setBusyUuid(null);
    if (res.success) setViewing({ uuid, json: JSON.stringify(res.data, null, 2) });
    else setRuntimeNote(res.error || 'Could not load that agent');
  };

  const adoptRuntime = async (uuid: string) => {
    setBusyUuid(uuid);
    const res = await aiAgentsService.adoptRuntimeAgent(uuid);
    setBusyUuid(null);
    if (!res.success) { setRuntimeNote(res.error || 'Adopt failed'); return; }
    // It now has a cpass record, so it leaves the runtime-only list.
    setRuntime(list => list.filter(r => r.agent_uuid !== uuid));
    load();
  };

  const remove = async (a: AiAgent) => {
    if (!a.id) return;
    const res = await aiAgentsService.remove(a.id);
    if (res.success) setAgents(list => list.filter(x => x.id !== a.id));
    else setError(res.error || 'Delete failed');
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2"><Bot className="w-6 h-6 text-blue-600" /> AI Agents</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Voice agents — each binds to the runtime (Redis) and exposes a wss URL you point an IVR flow at.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {/* Only a service provider sees this — it renders nothing when there is
              no other business to choose between. */}
          <BusinessSwitcher value={orgId} onChange={setOrgId} />
          <button onClick={load} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 inline-flex items-center gap-2"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          <button onClick={() => navigate('/agents/create')} className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New Agent</button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

      {loading && agents.length === 0 ? <LoadingSpinner /> : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>{['Name', 'Voice / Model', 'wsurl (IVR binding)', 'Status', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {agents.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">No agents yet. Click <b>New Agent</b>.</td></tr>}
                {agents.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 cursor-pointer" onClick={() => a.id && navigate(`/agents/${a.id}/config`)}><div className="text-sm font-medium text-blue-600 hover:underline">{a.name}</div><div className="text-xs text-gray-400">{a.description}</div></td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{a.voice || '—'} · {a.model || '—'}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs font-mono text-indigo-600 dark:text-indigo-300"><Link2 className="w-3 h-3" />{a.wsurl}</span></td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs rounded-full ${a.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{a.enabled ? 'Enabled' : 'Disabled'}</span></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => a.id && navigate(`/agents/${a.id}/config`)} title="Edit — full agent configuration" className="text-xs text-indigo-600 hover:underline mr-3">Edit</button>
                      <button onClick={() => remove(a)} title="Delete" className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Agents that live on the voice runtime but have no record here — created
          directly against the runtime, so the list above can never show them. */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <button
          onClick={toggleRuntime}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
            {showRuntime ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <Server className="w-4 h-4 text-indigo-500" />
            On the runtime, not in cpass
            {runtime.length > 0 && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700">{runtime.length}</span>
            )}
          </span>
          <span className="text-xs text-gray-400">
            {runtimeLoading ? 'Loading…'
              : !runtimeLoaded ? 'Click to check'
              : runtimeNote ? 'Unavailable'
              : runtime.length >= PAGE ? `first ${PAGE}` : `${runtime.length} found`}
          </span>
        </button>

        {showRuntime && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 overflow-x-auto">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Agents created straight against the voice runtime. Only those created since the runtime began
              recording which business an agent belongs to appear here — older ones carry no business
              reference and cannot be discovered.
            </p>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <input
                value={byId}
                onChange={e => setById(e.target.value)}
                placeholder="Agent ID or its wss:// URL"
                className="flex-1 min-w-[16rem] px-3 py-1.5 text-xs font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200"
              />
              <button onClick={() => byIdUuid && viewRuntime(byIdUuid)} disabled={!byIdUuid || busyUuid === byIdUuid}
                      className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {busyUuid === byIdUuid ? 'Opening…' : 'Open'}
              </button>
              {byId && !byIdUuid && <span className="text-xs text-amber-700">That does not contain an agent id.</span>}
            </div>

            {runtimeNote && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2 mb-3">
                {runtimeNote}
              </div>
            )}

            {runtime.length >= PAGE && (
              <div className="bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded-lg px-3 py-2 mb-3">
                Showing the {PAGE} most recent. A business can own tens of thousands of these, so the list is
                capped rather than loaded whole.
              </div>
            )}

            {runtimeLoading ? (
              <LoadingSpinner />
            ) : runtime.length === 0 && !runtimeNote ? (
              <p className="text-sm text-gray-500 py-2">Nothing on the runtime that isn't already here.</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr>{['Name', 'Agent UUID', 'Business', 'Created', ''].map(h => (
                    <th key={h} className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {runtime.map(r => (
                    <tr key={r.agent_uuid}>
                      <td className="px-2 py-2 text-sm text-gray-800 dark:text-gray-200">{r.agent_name || <span className="text-gray-400">—</span>}</td>
                      <td className="px-2 py-2 text-xs font-mono text-gray-600 dark:text-gray-300 break-all">{r.agent_uuid}</td>
                      <td className="px-2 py-2 text-xs font-mono text-gray-500">{r.business_id || '—'}</td>
                      <td className="px-2 py-2 text-xs text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <button onClick={() => viewRuntime(r.agent_uuid)} disabled={busyUuid === r.agent_uuid}
                                className="text-xs text-blue-600 hover:underline mr-3 disabled:opacity-50">View</button>
                        {/* Adopt creates the cpass record from the runtime's own
                            config and keeps the uuid, so the normal editor works. */}
                        <button onClick={() => adoptRuntime(r.agent_uuid)} disabled={busyUuid === r.agent_uuid}
                                className="text-xs text-indigo-600 hover:underline disabled:opacity-50"
                                title="Manage this agent in cpass">
                          {busyUuid === r.agent_uuid ? 'Working…' : 'Manage here'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Read-only config of a runtime agent, for one with no cpass record. */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Runtime agent</h3>
              <p className="text-xs font-mono text-gray-500 break-all mt-0.5">{viewing.uuid}</p>
              <p className="text-xs text-gray-500 mt-1">
                As the runtime holds it. Read-only — use <b>Manage here</b> to make it editable in cpass.
              </p>
            </div>
            <pre className="flex-1 overflow-auto text-[11px] font-mono p-4 text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all">
              {viewing.json}
            </pre>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              {/* Same adopt as the list's button: creates the cpass record from the runtime's own config. */}
              <button onClick={async () => { const u = viewing.uuid; await adoptRuntime(u); setViewing(null); }}
                      disabled={busyUuid === viewing.uuid}
                      className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      title="Manage this agent in cpass">
                {busyUuid === viewing.uuid ? 'Working…' : 'Manage here'}
              </button>
              <button onClick={() => setViewing(null)}
                      className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAgents;
